import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Fetches the CSRF token from the backend and caches it in memory.
 *
 * The XSRF-TOKEN cookie set by Spring cannot be reliably read via
 * document.cookie when the SPA and the API run on different ports (cross-origin),
 * because the cookie is scoped to the API origin. Instead, this service fetches
 * the token once via a public GET endpoint and holds it in memory.
 *
 * Usage: AuthInterceptor reads getToken() synchronously for every mutating
 * request. Call fetchToken() once after login to prime the cache.
 */
@Injectable({ providedIn: 'root' })
export class CsrfService {

  private token: string | null = null;

  constructor(private http: HttpClient) { }

  /**
   * Returns the cached CSRF token, or null if not yet fetched.
   * Synchronous — designed to be called from an HttpInterceptor.
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Fetches the current CSRF token from the backend and caches it.
   * Must be called once after a successful login before any write operation.
   */
  async fetchToken(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ token: string }>(
          `${environment.apiUrl}/public/csrf`,
          { withCredentials: true }
        )
      );
      this.token = response?.token ?? null;
    } catch {
      // Non-fatal: writes will fail with 403 if the token is unavailable,
      // which will surface as a normal API error to the user.
      this.token = null;
    }
  }

  /** Clears the cached token (call on logout). */
  clearToken(): void {
    this.token = null;
  }
}
